import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IComprovantesVisao } from '../../interfaces/IModuloBancario';
import ArquivoOperacaoService from '../../services/ArquivoOperacaoService';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import { BarraFiltros, PaginacaoModulo, useFiltroLista } from './paginacao-modulo';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = nenhuma competência escolhida. */
    competencia: string;
    /** A geração das minutas muda o uso dos comprovantes. */
    versaoMinutas: number;
    /** Excluir ou ler comprovantes muda as minutas. */
    onAlterou: () => void;
}

const INTERVALO_MS = 4000;
const ACEITOS = '.pdf,.jpg,.jpeg,.png,.webp';

const SITUACAO: Record<string, { texto: string; classe: string }> = {
    ANALISANDO: { texto: 'Calculando custo', classe: 'badge-light' },
    AGUARDANDO_DECISAO: { texto: 'Aguardando decisão', classe: 'badge-light-warning' },
    LENDO: { texto: 'Lendo', classe: 'badge-light-primary' },
    LIDO: { texto: 'Lido', classe: 'badge-light-success' },
    DUPLICADO: { texto: 'Já enviado', classe: 'badge-light' },
    ERRO: { texto: 'Erro', classe: 'badge-light-danger' },
};

const LIDA: Record<string, string> = {
    LIQUIDADO: 'Liquidado',
    AGENDADO: 'Agendado',
    CANCELADO: 'Cancelado',
    INDEFINIDO: 'Não identificado',
};

const brl = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const usd = (valor: number) => `US$ ${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const VAZIO: IComprovantesVisao = { arquivos: [], comprovantes: [], minutasEsperandoEvidencia: 0, dispensaJustificativa: null, dispensaEm: null, declaracaoDispensa: '', totalItens: 0, pagina: 1, tamanhoPagina: 50 };

// DEVN3-772 — comprovantes da competência: envio em lote, custo antes de ler, situação de cada um e dispensa.
export default function ComprovantesBancarios({ esteiraId, competencia, versaoMinutas, onAlterou }: Props) {
    const [visao, setVisao] = useState<IComprovantesVisao>(VAZIO);
    const [enviando, setEnviando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [dispensa, setDispensa] = useState<{ justificativa: string; ciencia: boolean } | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [ano, mes] = competencia ? competencia.split('-').map(Number) : [0, 0];
    const filtro = useFiltroLista();

    const buscar = useCallback(async (silencioso = false) => {
        if (!ano || !mes) return;
        try {
            setVisao(await ModuloBancarioService.comprovantes(esteiraId, ano, mes, filtro.params));
        } catch (err: any) {
            if (!silencioso) Swal.fire({ text: err?.message ?? 'Não foi possível carregar os comprovantes.', icon: 'error' });
        }
    }, [esteiraId, ano, mes, filtro.params]);

    // O filtro muda a consulta, não a competência: a lista antiga fica na tela até a nova chegar.
    useEffect(() => {
        setVisao(VAZIO);
    }, [esteiraId, ano, mes]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    useEffect(() => {
        if (versaoMinutas > 0) buscar(true);
    }, [versaoMinutas, buscar]);

    const emAndamento = visao.arquivos.some(a => a.situacao === 'ANALISANDO' || a.situacao === 'LENDO');
    const leitura = visao.arquivos.some(a => a.situacao === 'LENDO');
    useEffect(() => {
        if (!emAndamento) return;
        const id = setInterval(() => buscar(true), INTERVALO_MS);
        return () => clearInterval(id);
    }, [emAndamento, buscar]);

    // Terminada a leitura, a geração refaz as minutas: a lista precisa acompanhar.
    const liaAntes = useRef(false);
    useEffect(() => {
        if (liaAntes.current && !leitura) onAlterou();
        liaAntes.current = leitura;
    }, [leitura, onAlterou]);

    const enviar = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const files = ev.target.files;
        if (!files || files.length === 0) return;

        setEnviando(true);
        const falhas: string[] = [];
        for (const file of Array.from(files)) {
            try {
                const { signedUrl, generatedFileName } = await ArquivoOperacaoService.gerarUrl(file.name, file.type || 'application/octet-stream');
                await ArquivoOperacaoService.enviarParaStorage(signedUrl, file);
                await ModuloBancarioService.registrarComprovante(esteiraId, { ano, mes, nomeGerado: generatedFileName, nomeOriginal: file.name });
            } catch (err: any) {
                falhas.push(`${file.name}: ${err?.message ?? 'falha no envio'}`);
            }
        }
        setEnviando(false);
        if (inputRef.current) inputRef.current.value = '';
        if (falhas.length > 0) Swal.fire({ title: 'Alguns arquivos não foram enviados', html: falhas.join('<br/>'), icon: 'warning' });
        buscar();
    };

    const executar = async (acao: () => Promise<unknown>, erro: string) => {
        setSalvando(true);
        try {
            await acao();
            await buscar(true);
            return true;
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? erro, icon: 'error' });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    const aguardando = visao.arquivos.filter(a => a.situacao === 'AGUARDANDO_DECISAO');
    const custoProvavel = aguardando.reduce((s, a) => s + (a.custoProvavelUsd ?? 0), 0);
    const custoMaximo = aguardando.reduce((s, a) => s + (a.custoMaximoUsd ?? 0), 0);

    const decidir = (processar: boolean, ids = aguardando.map(a => a.id)) =>
        executar(() => ModuloBancarioService.decidirComprovantes(esteiraId, ids, processar), 'Não foi possível registrar a decisão.');

    const excluir = async (id: number) => {
        const confirmacao = await Swal.fire({ text: 'Excluir este comprovante?', icon: 'question', showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar' });
        if (!confirmacao.isConfirmed) return;
        if (await executar(() => ModuloBancarioService.excluirComprovante(esteiraId, id), 'Não foi possível excluir.')) onAlterou();
    };

    const confirmarDispensa = async () => {
        if (!dispensa) return;
        const ok = await executar(
            () => ModuloBancarioService.dispensarComprovantes(esteiraId, { ano, mes, justificativa: dispensa.justificativa, ciencia: dispensa.ciencia }),
            'Não foi possível dispensar.');
        if (ok) setDispensa(null);
    };

    const nomeArquivo = (id: number) => visao.arquivos.find(a => a.id === id)?.nomeOriginal ?? '';

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Comprovantes
                    {(enviando || emAndamento) && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                {competencia && (
                    <div className='d-flex gap-2'>
                        {!visao.dispensaEm && (
                            <button className='btn btn-sm btn-light' onClick={() => setDispensa({ justificativa: '', ciencia: false })}>Dispensar</button>
                        )}
                        <input ref={inputRef} type='file' multiple accept={ACEITOS} className='d-none' onChange={enviar} />
                        <button className='btn btn-sm btn-light-primary' disabled={enviando} onClick={() => inputRef.current?.click()}>
                            <i className='fas fa-upload'></i> Enviar comprovantes
                        </button>
                    </div>
                )}
            </div>
            <div className='card-body pt-0'>
                {!competencia ? (
                    <div className='text-muted'>Escolha a competência no topo da página.</div>
                ) : (
                    <>
                        <div className='text-muted fs-7 mb-3'>
                            PIX, TED e boleto em PDF ou imagem. O comprovante escolhe entre provisões do mesmo valor e fica como evidência da minuta. Sem linha no extrato, não gera minuta.
                        </div>

                        {visao.dispensaEm ? (
                            <div className='notice bg-light rounded p-3 mb-3 fs-7'>
                                Comprovantes dispensados em {moment.utc(visao.dispensaEm).local().format('DD/MM/YYYY')}: {visao.dispensaJustificativa}
                            </div>
                        ) : visao.minutasEsperandoEvidencia > 0 && (
                            <div className='notice bg-light-warning rounded p-3 mb-3 fs-7'>
                                {visao.minutasEsperandoEvidencia} minuta(s) desta competência esperando evidência.
                            </div>
                        )}

                        {aguardando.length > 0 && (
                            <div className='d-flex flex-wrap align-items-center gap-3 border rounded p-3 mb-3'>
                                <span className='fs-7 flex-grow-1'>
                                    {aguardando.length} arquivo(s) aguardando decisão. Custo provável de <strong>{usd(custoProvavel)}</strong>, no máximo {usd(custoMaximo)}.
                                </span>
                                <button className='btn btn-sm btn-light' disabled={salvando} onClick={() => decidir(false)}>Descartar</button>
                                <button className='btn btn-sm btn-primary' disabled={salvando} onClick={() => decidir(true)}>Processar</button>
                            </div>
                        )}

                        {visao.arquivos.filter(a => a.situacao !== 'LIDO').map(a => (
                            <div key={a.id} className='d-flex align-items-center gap-2 fs-7 mb-2'>
                                <span className={`badge ${SITUACAO[a.situacao]?.classe ?? 'badge-light'}`}>{SITUACAO[a.situacao]?.texto ?? a.situacao}</span>
                                <span>{a.nomeOriginal}</span>
                                {a.mensagem && <span className='text-muted'>{a.mensagem}</span>}
                                {(a.situacao === 'ERRO' || a.situacao === 'DUPLICADO') && (
                                    <button className='btn btn-sm btn-link p-0 ms-auto' disabled={salvando} onClick={() => decidir(false, [a.id])}>Tirar da lista</button>
                                )}
                            </div>
                        ))}

                        {(visao.totalItens > 0 || filtro.ativo) && (
                            <BarraFiltros filtro={filtro} placeholder='Buscar por favorecido, documento ou arquivo' situacoes={LIDA} />
                        )}
                        {visao.comprovantes.length === 0 && filtro.ativo && <div className='text-muted py-4'>Nenhum comprovante com estes filtros.</div>}

                        {visao.comprovantes.length > 0 && (
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2 mt-3'>
                                <thead>
                                    <tr className='fw-bold text-muted'>
                                        <th>Liquidação</th>
                                        <th>Tipo</th>
                                        <th>Favorecido ou pagador</th>
                                        <th>Documento</th>
                                        <th className='text-end'>Valor</th>
                                        <th>Situação</th>
                                        <th>Uso</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visao.comprovantes.map(c => (
                                        <tr key={c.id}>
                                            <td className='fs-7 text-nowrap'>{c.dataLiquidacao ? moment(c.dataLiquidacao).format('DD/MM/YYYY') : '—'}</td>
                                            <td className='fs-7'>{c.tipo ?? '—'} · {c.direcao === 'RECEBIMENTO' ? 'Recebimento' : 'Pagamento'}</td>
                                            <td className='fs-7'>
                                                {c.nome ?? '—'}
                                                {c.documentoMascarado && <div className='text-muted fs-8'>{c.documentoMascarado}</div>}
                                            </td>
                                            <td className='fs-7'>{c.numeroDocumento ?? '—'}</td>
                                            <td className='text-end text-nowrap fs-7'>{brl(c.valor)}</td>
                                            <td style={{ maxWidth: 240 }}>
                                                <span className={`badge ${c.valido ? 'badge-light-success' : 'badge-light-danger'}`}>{LIDA[c.situacaoLida] ?? c.situacaoLida}</span>
                                                {c.motivoInvalido && <div className='text-muted fs-8 mt-1'>{c.motivoInvalido}</div>}
                                            </td>
                                            <td className='fs-7'>{c.minutaId ? 'Em minuta' : c.valido ? 'Sem linha casada' : '—'}</td>
                                            <td className='text-end text-nowrap'>
                                                <button className='btn btn-sm btn-icon btn-light me-1' title={`Abrir ${nomeArquivo(c.arquivoId)}`}
                                                    onClick={() => ModuloBancarioService.baixarComprovante(esteiraId, c.arquivoId, nomeArquivo(c.arquivoId))
                                                        .catch((err: any) => Swal.fire({ text: err?.message ?? 'Não foi possível abrir o arquivo.', icon: 'error' }))}>
                                                    <i className='fas fa-file-download'></i>
                                                </button>
                                                <button className='btn btn-sm btn-icon btn-light-danger' title='Excluir' disabled={salvando} onClick={() => excluir(c.id)}>
                                                    <i className='fas fa-trash'></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <PaginacaoModulo total={visao.totalItens} pagina={filtro.pagina} tamanhoPagina={filtro.tamanhoPagina} onPagina={filtro.setPagina} onTamanho={filtro.setTamanho} />
                    </>
                )}
            </div>

            {dispensa && (
                <Modal show onHide={() => setDispensa(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Dispensar comprovantes</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='text-muted fs-7 mb-3'>Vale para toda a empresa nesta competência. As minutas e o fechamento não mudam.</div>
                        <label className='form-label'>Justificativa</label>
                        <textarea className='form-control mb-3' rows={3} maxLength={500} value={dispensa.justificativa}
                            onChange={e => setDispensa({ ...dispensa, justificativa: e.target.value })}></textarea>
                        <label className='form-check form-check-custom'>
                            <input type='checkbox' className='form-check-input' checked={dispensa.ciencia} onChange={e => setDispensa({ ...dispensa, ciencia: e.target.checked })} />
                            <span className='form-check-label fs-7'>{visao.declaracaoDispensa}</span>
                        </label>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setDispensa(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={salvando || !dispensa.ciencia || !dispensa.justificativa.trim()} onClick={confirmarDispensa}>Dispensar</button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}
